import { useObjectContext } from '@context/ItemZoneContext'
import { useAppStore } from '@store'
import * as Accordion from '@radix-ui/react-accordion'
import { TexturesGroup } from '@types'
import clsx from 'clsx'

const paddingDictionary = {
  0: 'pl-0',
  1: 'pl-[1.25rem]',
  2: 'pl-[2.5rem]',
  3: 'pl-[3.75rem]',
  4: 'pl-[5rem]',
} as const

// Nombres de grupos de texturas que NO deben mostrarse para areas y walls
const EXCLUDED_GROUP_NAMES = [
  'Alfombras',
  'Cristales',
  'Cuadros',
  'Encimeras',
  'Maderas',
  'Marmol',
  'Varios',
]

type Props = {
  groups?: any
  nestingLevel: number
}

export default function GroupsAccordion({ groups, nestingLevel }: Props) {
  const {
    controlledValues,
    setControlledValues,
    currentTexture,
    centerInfo,
    actions: { handleGroupClick },
  } = useObjectContext()

  const currentObjectID = useAppStore((s) => s.currentObjectID)
  const selected = useAppStore((s) => s.items.get(currentObjectID) ?? s.areas.get(currentObjectID))
  const isAreaOrWall = selected?.type === 'areas' || selected?.type === 'walls'

  const toRender = groups ?? centerInfo
  if (!toRender || !Array.isArray(toRender)) return null

  return toRender.map((group: TexturesGroup) => {
    if (isAreaOrWall && nestingLevel === 0 && EXCLUDED_GROUP_NAMES.includes(group.nombre)) {
      return null
    }
    if (!group || !group.idGruposDeTexturas) {
      return null
    }

    const hasSubGroups = group.subGrupos.length > 0
    const groupID = group.idGruposDeTexturas

    if (!currentTexture) return null

    const isCurrentZoneGroup = controlledValues.includes(groupID)

    return (
      <Accordion.Item
        value={groupID}
        key={groupID}
      >
        <Accordion.Header>
          <Accordion.Trigger
            onClick={() => {
              if (hasSubGroups) {
                const newValue = controlledValues.includes(groupID) ? '' : groupID
                setControlledValues((prev) => {
                  const newValues = prev.slice(0, nestingLevel + 1)
                  newValues[nestingLevel] = newValue
                  return newValues
                })
              } else {
                handleGroupClick(groupID)
              }
            }}
            style={{
              backgroundColor: isCurrentZoneGroup
                ? `rgb(${217 - nestingLevel * 10}, ${217 - nestingLevel * 10}, ${217 - nestingLevel * 10})`
                : '',
            }}
            className={clsx(
              'relative w-full border-b border-C/Color2 pb-1.5 pl-1 pt-1.5 text-left',
              'hover-bg-C/Color2 cursor-pointer',
              controlledValues.includes(groupID) && 'font-semibold',
              isCurrentZoneGroup && 'bg-C/Color2',
            )}
          >
            <span className={paddingDictionary[nestingLevel] ?? 'pl-0'}>{group.nombre}</span>
            {hasSubGroups ? ' >' : ''}
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className='AccordionContent'>
          {hasSubGroups && (
            <Accordion.Root
              type='single'
              collapsible
              value={controlledValues[nestingLevel + 1]}
              onValueChange={(value) => {
                const newValues = controlledValues.slice(0, nestingLevel + 1)
                newValues[nestingLevel + 1] = value
                setControlledValues(newValues)
              }}
            >
              <GroupsAccordion
                groups={group.subGrupos}
                nestingLevel={nestingLevel + 1}
              />
            </Accordion.Root>
          )}
        </Accordion.Content>
      </Accordion.Item>
    )
  })
}
